import { TopicCategoryPicker } from './TopicCategoryPicker';
import React, { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { 
  PhotoIcon, 
  MusicalNoteIcon, 
  XMarkIcon,
  CloudArrowUpIcon,
  FolderIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import type { FassungsZustand } from '../../hooks/useFassungen';
import { AudioFormData, AudioStreamTypeLabels, VariantExtraTrack, VariantSlot } from '../../types/audio';
import { ProductType, ProductTypeLabels, audioFormularProduktarten, audioProductTypes } from '../../types/productType';
import { 
  ContentStatus, HeadphonesRecommendation, STATUS_ANZEIGE, STATUS_WAEHLBAR
} from '../../types';
import { SchalterDeckung } from '../content/SchalterDeckung';
import { FassungsUebersicht } from '../content/FassungsUebersicht';
import { BunnyFilePicker } from '../admin/BunnyFilePicker';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import type { BenachrichtigungsTyp } from '../ui/Benachrichtigung';
import { artistService } from '../../services/artistService';
import { Artist } from '../../types/artist';
import { SchlagwortFeld } from './SchlagwortFeld';
import { ModerationsHinweis } from './ModerationsHinweis';
import { PremiumFeld } from './PremiumFeld';
import { useDarfModerieren } from '../../hooks/useDarfModerieren';
import { fälltAufEntwurf, MODERATIONS_TEXTE, RÜCKFALL_RÜCKFRAGE, stufeGesperrt, stufenText } from '../../utils/moderation';

// Varianten-Slots für zusätzliche Spuren (deckt sich mit den Stream-Slots / Backend-kinds).
// Die Anzeigenamen kommen aus `AudioStreamTypeLabels`; hier steht nur noch die Reihenfolge,
// in der die Blöcke im Formular erscheinen — das ist das Einzige, was diese Liste festlegt.
const VARIANT_SLOT_ORDER: VariantSlot[] = ['audio', 'silent', 'frequenz', 'nature'];
const VARIANT_SLOTS: { slot: VariantSlot; label: string }[] = VARIANT_SLOT_ORDER.map((slot) => ({
  slot,
  label: AudioStreamTypeLabels[slot],
}));

// Historische Aufrufer könnten die Kopfhörer-Map noch verschachtelt mitgeben (metadata.
// headphonesByVariant bzw. headphonesByVariant auf oberster Ebene). AudioManagement flacht sie
// heute schon in die *Headphones-Felder aus; die Zweige bleiben trotzdem erhalten — hier nur
// typisiert statt über `as any` gelesen.
type HeadphonesByVariant = Partial<Record<VariantSlot, string>>;
const withLegacyHeadphones = (data: Partial<AudioFormData>) =>
  data as Partial<AudioFormData> & {
    metadata?: { headphonesByVariant?: HeadphonesByVariant };
    headphonesByVariant?: HeadphonesByVariant;
  };

// Reiner Helfer über die importierte `audioProductTypes`-Liste — wie `buildFormData`
// ohne Bezug zum Komponenten-Scope.
const normalizeProductCategory = (value?: string | null) => {
  if (!value) return undefined;
  if (audioProductTypes.includes(value as ProductType)) {
    return value as ProductType;
  }
  const normalized = value.toLowerCase();
  return audioProductTypes.find(category => category.toLowerCase() === normalized);
};

/**
 * Reine Abbildung Partial<AudioFormData> -> AudioFormData. Greift auf NICHTS aus dem
 * Komponenten-Scope zu und lag trotzdem darin — dadurch war sie bei jedem Render eine neue
 * Funktion und musste als Effekt-Abhängigkeit gelten. Auf Modulebene ist sie stabil.
 */
const buildFormData = (data: Partial<AudioFormData>): AudioFormData => ({
  title: data.title || '',
  description: data.description || '',
  shortDescription: data.shortDescription || '',
  duration: data.duration || '',
  headphones: data.headphones || HeadphonesRecommendation.NO,
  topicCategories: data.topicCategories || [],
  productCategory: normalizeProductCategory(data.productCategory as string | undefined),
  silentMp3Url: data.silentMp3Url || '',
  silentFlacUrl: data.silentFlacUrl || '',
  silentVolume: data.silentVolume ?? 100,
  silentEnabled: data.silentEnabled ?? false,
  audioMp3Url: data.audioMp3Url || '',
  audioVolume: data.audioVolume ?? 100,
  audioEnabled: data.audioEnabled ?? false,
  frequenzMp3Url: data.frequenzMp3Url || '',
  frequenzVolume: data.frequenzVolume ?? 100,
  frequenzEnabled: data.frequenzEnabled ?? false,
  natureMp3Url: data.natureMp3Url || '',
  natureVolume: data.natureVolume ?? 100,
  natureEnabled: data.natureEnabled ?? false,
  // Kopfhörer-Empfehlung pro Variante — aus metadata.headphonesByVariant laden (robust gegen Shape).
  audioHeadphones: data.audioHeadphones || withLegacyHeadphones(data).metadata?.headphonesByVariant?.audio || withLegacyHeadphones(data).headphonesByVariant?.audio || '',
  silentHeadphones: data.silentHeadphones || withLegacyHeadphones(data).metadata?.headphonesByVariant?.silent || withLegacyHeadphones(data).headphonesByVariant?.silent || '',
  frequenzHeadphones: data.frequenzHeadphones || withLegacyHeadphones(data).metadata?.headphonesByVariant?.frequenz || withLegacyHeadphones(data).headphonesByVariant?.frequenz || '',
  natureHeadphones: data.natureHeadphones || withLegacyHeadphones(data).metadata?.headphonesByVariant?.nature || withLegacyHeadphones(data).headphonesByVariant?.nature || '',
  artist: data.artist || '',
  artistId: data.artistId || '', // kuratierter Interpret (Dropdown); '' = kein Interpret
  album: data.album || '',
  language: data.language || '',
  coverImageUrl: data.coverImageUrl || '',
  // Premium ueberall (Tom 2026-09-11): neu → Premium; ein freies Werk bleibt frei (`??`, nicht `||`).
  isPremium: data.isPremium ?? true,
  tags: data.tags || [],
  affirmations: data.affirmations || '',
  frequencies: data.frequencies || '',
  // Zusätzliche Varianten-Spuren (N-pro-kind, position ≥1). IMMER als Array führen (auch leer), damit
  // Hinzufügen UND Entfernen greift (audioService sendet dann alle Slots explizit → Backend-replace).
  variantTracks: Array.isArray(data.variantTracks) ? data.variantTracks : [],
  // Status (Backend-Kompat) unverändert durchreichen: beim Bearbeiten den GELADENEN Status
  // erhalten. Bei NEU ist data.status undefined — der Dienst schickt dann keinen Status, und das
  // Backend setzt DRAFT selbst (seit `cms-status-oder-draft` kein `|| 'DRAFT'` mehr im Dienst).
  status: data.status,
});

/**
 * Was `FassungsUebersicht` beim ANLEGEN bekommt: nichts geladen, nichts behauptet.
 *
 * ⚠️ Lieber ein leerer Zustand als ein `fassungen &&` um die Fläche: die Komponente entscheidet
 * selbst, was sie bei leerem Zustand zeigt (nämlich nichts) — und diese Entscheidung soll an
 * EINER Stelle stehen, nicht an jedem Aufrufer.
 */
const LEERER_FASSUNGSZUSTAND = {
  uebersicht: null,
  laedt: false,
  fehler: null,
  erneut: () => {},
} as const;

interface AudioFormProps {
  initialData?: Partial<AudioFormData>;
  onSubmit: (data: AudioFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  /**
   * Die eingelesenen Fassungen des Werks — für die Datei-Deckung je Schalter.
   *
   * ⚠️ Kommt als Eigenschaft herein und wird NICHT hier geholt: dieselben Daten braucht die
   * `Fassungsliste` unter dem Formular. Zwei Abrufe wären das kleinere Übel; zwei Zustände sind
   * das größere — ein Schalter, der „keine Datei" sagt, während die Liste darunter die Datei
   * zeigt, macht die ganze Fläche unglaubwürdig. Geladen wird einmal in `AudioManagement`
   * (`useFassungen`).
   *
   * Optional, weil es beim ANLEGEN weder Kennung noch Dateien gibt.
   */
  fassungen?: FassungsZustand;
  // Nur im Edit-Modus gesetzt: ID des geladenen Audios + Lösch-Handler (Gefahren-Aktion).
  audioId?: string;
  onDelete?: (id: string) => void;
  /**
   * Meldeweg dieses Formulars: die SEITE hält den Zustand, das Formular ruft nur.
   *
   * ⚠️ Nicht optional, und das ist der Punkt. Vorher hielt dieses Formular seine eigene
   * `useBenachrichtigung` für die Fehler des Vorhör-Players — zwei Zustände auf einem Bildschirm.
   * `AudioManagement.handleFormSubmit` meldet im `catch` und lässt das Formular bewusst OFFEN
   * (sonst wären die Eingaben weg); sein Balken stand aber im Listen-Zweig, also gar nicht im
   * Baum. Ergebnis: die Nutzerin klickt „Audio speichern", der Server lehnt ab, und sichtbar
   * passiert nichts. Beides zusammen — der zweite Zustand hier und der Balken im falschen Zweig —
   * ließ sich nur als EINE Änderung auflösen: eine Meldungs-Quelle pro Bildschirm.
   *
   * Der Name ist bewusst `notify` und nicht `melde`: `AlbumForm`, `ArtistForm` und `PlaylistForm`
   * tragen genau diese Eigenschaft mit genau dieser Signatur. Ein vierter Name für dieselbe Sache
   * wäre der Anfang derselben Drift, die dieser Umbau gerade beendet. Gereicht wird das `melde`
   * aus `useBenachrichtigung` — die Signaturen decken sich, deshalb passt es ohne Adapter.
   *
   * ⚠️ Und bewusst NUR melden, kein Gegenstück zum Wegräumen: die Quelle gehört jetzt der Seite,
   * und ein `schließe` aus diesem Formular träfe zwangsläufig auch eine Speicher-Fehlermeldung
   * derselben Quelle. Was das für den Vorhör-Player heißt, steht an `handlePlayPreview`.
   */
  notify: (type: BenachrichtigungsTyp, message: string) => void;
}

export const AudioForm: React.FC<AudioFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  audioId,
  onDelete,
  notify,
  fassungen
}) => {
  const [formData, setFormData] = useState<AudioFormData>(
    buildFormData(initialData)
  );

  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    initialData.coverImage ? URL.createObjectURL(initialData.coverImage) : (initialData.coverImageUrl || null)
  );
  const [showBunnyPicker, setShowBunnyPicker] = useState(false);
  const [bunnyPickerContext, setBunnyPickerContext] = useState<{
    type: 'image' | 'audio';
    stream?: 'silentMp3' | 'silentFlac' | 'audioMp3' | 'frequenzMp3' | 'natureMp3';
    // Bei zusätzlichen Varianten-Spuren: Index in formData.variantTracks statt eines fixen stream-Feldes.
    extraIndex?: number;
  }>({ type: 'image' });

  // Sicherheitsabfrage dieses Formulars. Feldname `rückfrage`, nicht `inhalt` — letzteres ist in
  // den umgestellten Dateien für den Benachrichtigungs-Balken belegt.
  //
  // ⚠️ Stapelordnung, hier gemessen und nicht angenommen: die Frage wird aus `handleSubmit`
  // gestellt, und `handleSubmit` ist per Tastatur (Enter in einem Eingabefeld) auch dann
  // auslösbar, wenn der Datei-Wähler offen davor liegt — der deckt zwar die Klicks ab, hält aber
  // keinen Fokus fest. Die Werte: Knopfleiste unten `z-30`, `.bunny-file-picker-overlay`
  // `fixed inset-0 z-50` (index.css), Rückfrage `z-[55]`, Balken `z-[60]`. Alle im selben
  // Stapelkontext — auf dem Weg `body → #root → .min-h-screen → main.main-content →
  // .page-content` steht weder `transform` noch `opacity` noch ein eigener `z-index`. Die
  // Rückfrage liegt damit über dem Wähler und unter dem Balken.
  const { rückfrage, frage, antworte } = useRueckfrage();

  // Kuratierte Interpreten fürs Künstler-Dropdown (Admin-Liste, alle Status → auch Entwürfe wählbar).
  const [artists, setArtists] = useState<Artist[]>([]);
  useEffect(() => {
    let alive = true;
    artistService
      .getArtists()
      .then((list) => { if (alive) setArtists(list); })
      .catch(() => { /* Dropdown bleibt leer — Künstler bleibt optional */ });
    return () => { alive = false; };
  }, []);

  // Hinweis am Titel-Feld. Eigener Zustand, KEIN zweiter Meldungs-Balken: ein Feld-Hinweis ist
  // etwas anderes als eine Seiten-Meldung — er steht am Feld, verschwindet mit der Eingabe und
  // konkurriert nicht mit der Speicher-Fehlermeldung der Seite. `uploadError` in `VideoForm` und
  // `csvError` in `MindBookForm` fahren dieselbe Bauform.
  const [titelFehler, setTitelFehler] = useState<string | null>(null);
  // Die Kennung verdrahtet Feld und Hinweis (`aria-describedby`). Über `useId`, nicht als feste
  // Zeichenkette: doppelte `id`-Werte im Baum machen die Verdrahtung still kaputt — dieselbe
  // Begründung wie in `Filterleiste.tsx`.
  const titelFehlerId = useId();
  // Wurzel für die Feld-Beschriftungen dieses Formulars, gleiche Begründung wie eine Zeile höher.
  // ⚠️ Die Kennungen der Varianten-Kästen tragen zusätzlich `streamName`: `renderStreamUpload`
  // läuft VIERMAL im selben Baum (silent/audio/frequenz/nature). Ohne den Zusatz stünde jede
  // Kennung viermal da, und `htmlFor` fände jedes Mal das erste Feld — die drei anderen
  // Beschriftungen zeigten still auf das falsche Feld.
  const feldId = useId();
  // Was der Moderation gehört (Bezahlschranke, Veröffentlichen, Rückfall auf den Entwurf) — Regeln und
  // Texte in `utils/moderation.ts`. Der Bestand ist der Status, mit dem das Formular geöffnet wurde.
  const darfModerieren = useDarfModerieren();
  const bestandStatus = initialData.status;
  const rückfall = fälltAufEntwurf(darfModerieren, bestandStatus, formData.status);

  // ── Aktiv? Quelle da? — EINE Definition für BEIDE Meldungen ────────────────────────────────
  //
  // ⛔ NICHT ZURÜCK IN `handleSubmit` SCHIEBEN, auch wenn es dort „hingehört" aussieht.
  //
  // Diese zwei Prüfungen standen bis 2026-09-04 genau dort. Gehoben sind sie, weil ZWEI Leser
  // dieselbe Frage stellen: der Hinweis an der Zeile („aktiv, aber ohne Quelle") und der Guard
  // beim Absenden. Zwei eigene Fassungen derselben Regel driften — und zwar unbemerkt: der
  // Hinweis sagt „passt", das Speichern blockt, und niemand versteht warum. Genau diese zweite
  // Wahrheit hat in diesem Baum am selben Tag dreimal zugeschlagen (eine doppelt existierende
  // Stufen-Funktion, eine von vier `publishedAt`-Türen, die Status-Bedienung in fünf von sechs
  // Formularen).
  //
  // ⚠️ Die REGEL ist unverändert: gleiche Felder, gleiche Reihenfolge, gleiches Ergebnis.
  // Verschoben ist nur, WER sie lesen darf. Wer sie zurückschiebt, baut die zweite Fassung neu
  // auf — und die Wache, die das merkt, gibt es nicht.
  const istAktiv = (key: string): boolean =>
    Boolean(formData[`${key}Enabled` as keyof AudioFormData]);
  const hatQuelle = (key: string): boolean => {
    const mp3Url = (formData[`${key}Mp3Url` as keyof AudioFormData] as string | undefined)?.trim();
    const mp3File = formData[`${key}Mp3File` as keyof AudioFormData] as File | undefined;
    const flacUrl = (formData[`${key}FlacUrl` as keyof AudioFormData] as string | undefined)?.trim();
    const flacFile = formData[`${key}FlacFile` as keyof AudioFormData] as File | undefined;
    return Boolean(mp3Url || mp3File || flacUrl || flacFile);
  };

  const titelRef = useRef<HTMLInputElement>(null);
  // ⚠️ Ersetzt `document.getElementById('tagInput')` — die feste Kennung war genau die Falle,
  // gegen die `useId` steht.

  const coverImageRef = useRef<HTMLInputElement>(null);
  // Die vier „Einstellungen"-Knöpfe — für die Scroll-Verankerung beim Umschalten (siehe
  // `fassungUmschalten`). Als feste Karte statt als Liste, damit der Zugriff über den Slot-Namen
  // geht und nicht über einen Index, der beim nächsten Umsortieren still danebenzeigt.
  const audioEinstellungenRef = useRef<HTMLButtonElement>(null);
  const silentEinstellungenRef = useRef<HTMLButtonElement>(null);
  const frequenzEinstellungenRef = useRef<HTMLButtonElement>(null);
  const natureEinstellungenRef = useRef<HTMLButtonElement>(null);
  const knopfRefs: Record<VariantSlot, React.RefObject<HTMLButtonElement | null>> = {
    audio: audioEinstellungenRef,
    silent: silentEinstellungenRef,
    frequenz: frequenzEinstellungenRef,
    nature: natureEinstellungenRef,
  };

  const silentMp3Ref = useRef<HTMLInputElement>(null);
  const silentFlacRef = useRef<HTMLInputElement>(null);
  const audioMp3Ref = useRef<HTMLInputElement>(null);
  const frequenzMp3Ref = useRef<HTMLInputElement>(null);
  const natureMp3Ref = useRef<HTMLInputElement>(null);
  
  // Audio Player State für Vorhör-Funktion
  const [playingAudio, setPlayingAudio] = useState<{ stream: string; format: string } | null>(null);
  // ⚠️ Hier stand die zweite Meldungs-Quelle dieses Bildschirms: eine eigene `useBenachrichtigung`
  // samt eigenem `<Benachrichtigung schwebend>` am Ende des Baums. Sie ist ersatzlos weg — die
  // Fehler des Vorhör-Players gehen jetzt über `notify` an die Quelle der Seite. Begründung an
  // der Eigenschaft `notify` oben.
  const audioPlayerRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    setFormData(buildFormData(initialData));
    setCoverImagePreview(
      initialData.coverImage
        ? URL.createObjectURL(initialData.coverImage)
        : (initialData.coverImageUrl || null)
    );
  }, [initialData]);

  const handleInputChange = <K extends keyof AudioFormData>(field: K, value: AudioFormData[K]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (file: File, stream: string, format: 'mp3' | 'flac') => {
    const fieldName = `${stream}${format.charAt(0).toUpperCase() + format.slice(1)}File` as keyof AudioFormData;
    setFormData(prev => ({ ...prev, [fieldName]: file }));
    // Reset URL when file is uploaded
    const urlFieldName = `${stream}${format.charAt(0).toUpperCase() + format.slice(1)}Url` as keyof AudioFormData;
    setFormData(prev => ({ ...prev, [urlFieldName]: undefined }));
  };

  const handleBunnyFileSelect = (url: string) => {
    const { type, stream, extraIndex } = bunnyPickerContext;

    if (type === 'image') {
      setCoverImagePreview(url);
      setFormData(prev => ({ ...prev, coverImageUrl: url, coverImage: undefined }));
    } else if (extraIndex !== undefined) {
      // Datei einer zusätzlichen Varianten-Spur — nach Index in variantTracks setzen.
      updateExtraTrack(extraIndex, { mp3Url: url });
    } else if (stream) {
      const urlFieldName = `${stream}Url` as keyof AudioFormData;
      setFormData(prev => ({
        ...prev,
        [urlFieldName]: url,
        [`${stream}File`]: undefined
      }));
    }
  };

  const openBunnyPicker = (type: 'image' | 'audio', stream?: 'silentMp3' | 'silentFlac' | 'audioMp3' | 'frequenzMp3' | 'natureMp3') => {
    setBunnyPickerContext({ type, stream });
    setShowBunnyPicker(true);
  };

  // ── Zusätzliche Varianten-Spuren (N-pro-kind) ────────────────────────────────────────────────
  const extraTracks: VariantExtraTrack[] = Array.isArray(formData.variantTracks) ? formData.variantTracks : [];

  const addExtraTrack = (slot: VariantSlot) => {
    setFormData(prev => ({
      ...prev,
      variantTracks: [...(prev.variantTracks || []), { slot, title: '', mp3Url: '', enabled: true }],
    }));
  };

  const updateExtraTrack = (index: number, patch: Partial<VariantExtraTrack>) => {
    setFormData(prev => {
      const list = [...(prev.variantTracks || [])];
      if (!list[index]) return prev;
      list[index] = { ...list[index], ...patch };
      return { ...prev, variantTracks: list };
    });
  };

  const removeExtraTrack = (index: number) => {
    setFormData(prev => {
      const list = [...(prev.variantTracks || [])];
      list.splice(index, 1);
      return { ...prev, variantTracks: list };
    });
  };

  const openBunnyPickerForExtra = (extraIndex: number) => {
    setBunnyPickerContext({ type: 'audio', extraIndex });
    setShowBunnyPicker(true);
  };

  const removeFile = (type: 'cover' | string, format?: 'mp3' | 'flac') => {
    if (type === 'cover') {
      setFormData(prev => ({ ...prev, coverImage: undefined, coverImageUrl: undefined }));
      setCoverImagePreview(null);
    } else {
      // Konstruiere die korrekten Feldnamen
      // type ist z.B. "silentMp3" oder "silentFlac"
      const urlFieldName = `${type}Url` as keyof AudioFormData;
      const fileFieldName = `${type}File` as keyof AudioFormData;
      
      // Setze URL und File zurück
      setFormData(prev => {
        // Die Feldnamen entstehen zur Laufzeit aus `type` (z. B. "silentMp3" → "silentMp3Url").
        // TypeScript kann eine solche Zuweisung nicht prüfen; `Record<string, unknown>` sagt
        // genau das — im Gegensatz zu `any`, das ringsum jede Prüfung mit abschaltet.
        const updated = { ...prev } as Record<string, unknown>;
        updated[urlFieldName] = '';
        updated[fileFieldName] = undefined;
        return updated as unknown as AudioFormData;
      });
      
      // Reset file input
      if (format === 'mp3') {
        const mp3Ref = type === 'silentMp3' ? silentMp3Ref : 
                       type === 'audioMp3' ? audioMp3Ref :
                       type === 'frequenzMp3' ? frequenzMp3Ref : natureMp3Ref;
        if (mp3Ref.current) {
          mp3Ref.current.value = '';
        }
      } else if (format === 'flac' && type === 'silentFlac' && silentFlacRef.current) {
        silentFlacRef.current.value = '';
      }
      
      // Stoppe Audio-Player, wenn die Datei entfernt wird
      if (playingAudio && playingAudio.stream === type && playingAudio.format === format) {
        setPlayingAudio(null);
        if (audioPlayerRef.current) {
          audioPlayerRef.current.pause();
          audioPlayerRef.current.src = '';
        }
      }
    }
  };
  
  // Vorhör-Funktion für Audio-URLs.
  //
  // ⚠️ Alle Fehler dieser Funktion gehen über `notify` an die Meldungs-Quelle der SEITE. Was hier
  // bewusst NICHT mehr passiert: das Wegräumen einer stehenden Meldung. Vorher rief dieses
  // Formular an fünf Stellen sein eigenes `schließe` (beim Anhalten, beim Starten, und aus
  // `onEnded`/`onLoadStart`/`onCanPlay` des versteckten Players). Auf einer geteilten Quelle wäre
  // genau das ein neuer Fehler derselben Art: ein Druck auf „Vorhören" löschte dann die
  // Fehlermeldung des letzten Speicherversuchs, ohne dass die Nutzerin sie weggeklickt hat.
  // Stattdessen gilt die Regel des geteilten Hakens — ein Fehler bleibt stehen, bis die Nutzerin
  // ihn schließt oder eine neue Meldung ihn ersetzt.
  const handlePlayPreview = async (url: string, stream: string, format: string) => {
    if (!url || url.trim() === '') {
      notify('error', 'Keine URL angegeben');
      return;
    }

    // Validiere URL-Format
    let validUrl: string;
    try {
      const urlObj = new URL(url);
      validUrl = urlObj.href;
    } catch {
      notify('error', 'Ungültige URL-Format');
      return;
    }
    
    const currentPlaying = `${stream}-${format}`;
    const newPlaying = { stream, format };
    
    if (playingAudio && `${playingAudio.stream}-${playingAudio.format}` === currentPlaying) {
      // Stoppe, wenn bereits abgespielt
      setPlayingAudio(null);
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.src = '';
      }
    } else {
      // Starte neues Audio
      setPlayingAudio(newPlaying);
      
      // Prüfe zuerst, ob die URL erreichbar ist
      try {
        await fetch(validUrl, { method: 'HEAD', mode: 'no-cors' });
        // HEAD Request funktioniert möglicherweise nicht wegen CORS, aber wir versuchen es
      } catch {
        // Ignoriere CORS-Fehler beim HEAD Request, da das Audio-Element das selbst prüft
        console.log('HEAD Request fehlgeschlagen (wahrscheinlich CORS), versuche trotzdem Audio zu laden');
      }
      
      if (audioPlayerRef.current) {
        // Setze crossOrigin für CORS
        audioPlayerRef.current.crossOrigin = 'anonymous';
        
        // Event-Handler für bessere Fehlerbehandlung
        const handleError = () => {
          const audioElement = audioPlayerRef.current;
          if (!audioElement) return;
          
          let errorMessage = 'Fehler beim Laden des Audios';
          
          if (audioElement.error) {
            switch (audioElement.error.code) {
              case MediaError.MEDIA_ERR_ABORTED:
                errorMessage = 'Audio-Wiedergabe wurde abgebrochen';
                break;
              case MediaError.MEDIA_ERR_NETWORK:
                errorMessage = 'Netzwerkfehler. Die Audio-Datei konnte nicht geladen werden. Möglicherweise ein CORS-Problem oder die Datei existiert nicht.';
                break;
              case MediaError.MEDIA_ERR_DECODE:
                errorMessage = 'Audio-Datei konnte nicht dekodiert werden. Möglicherweise ist die Datei beschädigt.';
                break;
              case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                errorMessage = `Audio-Format wird nicht unterstützt oder die URL ist ungültig. URL: ${validUrl}`;
                // Prüfe, ob die URL auf eine existierende Datei zeigt
                console.error('Audio-URL Problem:', {
                  url: validUrl,
                  format: format,
                  networkState: audioElement.networkState,
                  readyState: audioElement.readyState,
                  error: audioElement.error
                });
                break;
              default:
                errorMessage = `Unbekannter Fehler (Code: ${audioElement.error.code})`;
            }
          }
          
          notify('error', errorMessage);
          setPlayingAudio(null);
        };
        
        // Entferne alte Event-Handler
        audioPlayerRef.current.removeEventListener('error', handleError);
        audioPlayerRef.current.addEventListener('error', handleError);
        
        // Setze URL und lade
        audioPlayerRef.current.src = validUrl;
        audioPlayerRef.current.load();
        
        // Versuche abzuspielen
        audioPlayerRef.current.play().catch(err => {
          console.error('Fehler beim Abspielen:', err);
          let errorMessage = 'Fehler beim Abspielen des Audios';
          
          if (err.name === 'NotAllowedError') {
            errorMessage = 'Browser blockiert das Abspielen. Bitte erlauben Sie Audio-Wiedergabe.';
          } else if (err.name === 'NotSupportedError') {
            errorMessage = `Audio-Format wird nicht unterstützt. URL: ${validUrl}`;
          } else if (err.name === 'NetworkError') {
            errorMessage = `Netzwerkfehler. Die Audio-Datei konnte nicht geladen werden. URL: ${validUrl}`;
          } else if (err.name === 'AbortError') {
            errorMessage = 'Audio-Wiedergabe wurde abgebrochen.';
          }
          
          notify('error', errorMessage);
          setPlayingAudio(null);
        });
      }
    }
  };
  
  // Stoppe Audio, wenn Komponente unmountet.
  //
  // Der Ref wird beim Effekt-Lauf in eine lokale Variable kopiert, statt ihn im Cleanup erneut
  // zu lesen. Das ist hier NUR deshalb gleichwertig, weil das `<audio>`-Element unbedingt
  // gerendert wird (versteckter Vorhör-Player weiter unten im JSX) — `current` zeigt also ab
  // dem Mount auf denselben Knoten und wechselt nie. Wäre das Element bedingt gerendert, würde
  // die Kopie beim Mount `null` einfangen und das Aufräumen still nichts mehr tun.
  useEffect(() => {
    const player = audioPlayerRef.current;
    return () => {
      if (player) {
        player.pause();
        player.src = '';
      }
    };
  }, []);



  // ⚠️ `async` erst mit der Rückfrage: der native Browser-Dialog gab seinen `boolean` synchron
  // zurück, `frage` gibt ein Versprechen. `e.preventDefault()` steht weiterhin als ERSTE Anweisung und
  // läuft damit vor dem ersten `await` — täte es das nicht, schickte der Browser das Formular ab,
  // bevor überhaupt jemand gefragt wäre.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // TITEL — Hinweis AM FELD, nicht als Dialog und nicht als Balken.
    //
    // Hier stand `alert('Bitte geben Sie einen Titel ein')`. Der Hinweis gehört an das Feld, das
    // ihn auslöst: es ist ein einzelnes, benanntes Feld ganz oben in „Grunddaten", unbedingt
    // gerendert, in keinem eingeklappten Bereich und in keinem zweiten Reiter — nachgemessen, denn
    // ein Feld-Hinweis an einem unsichtbaren Feld wäre genau der Fehler, den diese Schiene
    // aufräumt.
    //
    // ⚠️ Der Zweig ist NICHT tot, obwohl das Feld `required` trägt. Nachgemessen unter jsdom:
    // ein leeres Pflichtfeld blockt das Absenden (0 Aufrufe von `onSubmit`), NUR-LEERZEICHEN
    // dagegen laufen durch (1 Aufruf) — `required` prüft auf leer, nicht auf Inhalt. Genau diese
    // Lücke deckt die Prüfung hier ab, und nur sie.
    //
    // Der Fokus wandert mit: ohne ihn stünde der Hinweis zwar sichtbar da, die Nutzerin bliebe
    // aber mit dem Fokus auf dem Absende-Knopf am unteren Rand stehen.
    if (!formData.title || formData.title.trim() === '') {
      setTitelFehler('Bitte einen Titel eingeben.');
      titelRef.current?.focus();
      return;
    }
    setTitelFehler(null);

    // PRODUKTKATEGORIE — bewusst OHNE eigene Meldung.
    //
    // Hier stand `alert('Bitte wählen Sie eine Produktkategorie aus')`. Die Auswahl unten trägt
    // `required` und ist immer bedienbar (nicht `disabled`), also lehnt der Browser das Absenden
    // selbst ab und schreibt seinen Hinweis an das Feld — nachgemessen unter jsdom: 0 Aufrufe von
    // `onSubmit` bei leerer Auswahl. Eine zweite Prüfung an dieser Stelle wäre unerreichbar und
    // damit eine Meldung, die niemand je zu sehen bekommt.
    //
    // Die Leerzeichen-Lücke des Titels gibt es hier nicht: die Werte stammen aus einer festen
    // Optionsliste, „nur Leerzeichen" ist kein möglicher Wert.

    // Guard: eine AKTIVIERTE Variante MUSS eine Audioquelle haben, sonst ist der Content nicht
    // abspielbar. Der Add-to-Playlist-Flow wählt die aktivierte Variante — zeigt sie ins Leere,
    // landet ein toter Playlist-Eintrag („Lebensenergie"-Klasse: enabled=true bei leerer URL).
    // Reihenfolge der Aufzählung in der Meldung; die Namen kommen aus `AudioStreamTypeLabels`.
    const VARIANTS: { key: VariantSlot; label: string }[] = (
      ['silent', 'audio', 'frequenz', 'nature'] as VariantSlot[]
    ).map((key) => ({ key, label: AudioStreamTypeLabels[key] }));
    // ⚠️ `istAktiv`/`hatQuelle` stehen auf Komponenten-Ebene, damit der frühe Hinweis an der
    // Zeile und dieser späte Guard DIESELBE Regel lesen. Hier stand vorher eine eigene Kopie.
    const isEnabled = istAktiv;
    const hasSource = hatQuelle;

    // VARIANTEN OHNE QUELLE — der geteilte Balken, nicht ein Hinweis am Feld.
    //
    // Hier stand ein `alert()` mit einer Aufzählung. Der Fehler gehört zu KEINEM einzelnen Feld:
    // er entsteht aus dem Zusammenspiel von bis zu vier Schaltern mit ihren jeweiligen Quellen und
    // nennt die Varianten als Liste. Ein Hinweis unter einem der vier MP3-Felder wäre die falsche
    // Stelle — und in diesem 1300-Zeilen-Formular liegt „Audio-Streams" weit unterhalb des
    // Sichtfelds, in dem die Nutzerin gerade den Absende-Knopf gedrückt hat.
    //
    // Der Balken der Seite ist im Formular-Zweig `schwebend` (`fixed top-4 right-4 z-[60]`,
    // Begründung und Messwerte in `Benachrichtigung.tsx` und `AudioManagement.tsx`). Er steht damit
    // unabhängig von der Bildlaufposition im Sichtfeld und über dem Datei-Wähler.
    //
    // ⚠️ Die Aufzählung wird zu einer Zeile: der Balken rendert seinen Text in EIN `<span>`, ein
    // `\n` fiele dort kommentarlos zusammen. Was im Dialog eine Liste war, ist hier eine Aufzählung
    // mit Komma — dieselbe Information, in der Form, die die Fläche tatsächlich darstellen kann.
    const enabledWithoutSource = VARIANTS.filter((v) => isEnabled(v.key) && !hasSource(v.key));
    if (enabledWithoutSource.length > 0) {
      const namen = enabledWithoutSource.map((v) => v.label).join(', ');
      notify(
        'error',
        `Ohne Audiodatei nicht abspielbar: ${namen}. Bitte je eine Datei oder URL hinterlegen `
          + 'oder die Variante deaktivieren.'
      );
      return;
    }

    // Weicher Hinweis (überstimmbar): als „Silent Subliminal" deklariert, aber die Silent-Variante
    // ist nicht aktiv — stattdessen eine andere. Meist ein vertauschter Toggle (Wurzelursache des
    // „Lebensenergie"-Bugs). Kein Hard-Block, falls es bewusst so gewollt ist.
    if (
      formData.productCategory === 'silent-subliminal' &&
      !isEnabled('silent') &&
      (isEnabled('audio') || isEnabled('frequenz') || isEnabled('nature'))
    ) {
      // Bewusst OHNE `gefahr`: hier wird nichts gelöscht und nichts abgeschaltet, es wird
      // gespeichert. Der Stand bleibt danach vollständig bearbeitbar — der vertauschte Schalter
      // ist mit zwei Klicks korrigiert. Rot wäre an dieser Stelle eine Warnung ohne Verlust
      // dahinter und entwertete die Farbe dort, wo tatsächlich etwas verschwindet.
      const trotzdem = await frage({
        titel: 'Silent-Variante ist nicht aktiviert',
        text:
          'Dieses Produkt ist als „Silent Subliminal" deklariert, aber die Silent-Variante ist '
          + 'nicht aktiviert — aktiv ist stattdessen eine andere Variante. Meist steckt dahinter '
          + 'ein vertauschter Schalter.\n\n'
          + 'Gespeichert wird das Produkt genau so, wie es hier steht; ändern lässt es sich '
          + 'danach jederzeit.',
        bestätigenText: 'Trotzdem speichern',
      });
      if (!trotzdem) return;
    }

    // F3: speichert die Redaktion ein veröffentlichtes Werk, fällt es auf den Entwurf — vorher fragen,
    // nicht hinterher melden. Begründung an `RÜCKFALL_RÜCKFRAGE`.
    if (rückfall && !(await frage(RÜCKFALL_RÜCKFRAGE))) return;

    // ⚠️ `formData` stammt aus der Darstellung, in der geklickt wurde — und genau das ist
    // richtig: gespeichert wird der Stand, über den gefragt wurde. Ändern kann er sich während
    // der Frage ohnehin nicht, der Schleier der Rückfrage deckt die Eingabemaske ab und fängt
    // den Fokus.
    onSubmit(formData);
  };


  // Der Parameter ist ein Slug aus der Datenbank, kein Enum-Wert mehr — die Taxonomie ist zur
  // Laufzeit pflegbar, ein Compile-Zeit-Typ kann sie nicht mehr abbilden. Die Gültigkeit
  // sichert stattdessen die Auswahl-Komponente (nur bekannte oder bereits gesetzte Slugs).
  const handleSubTopicToggle = (subTopic: string) => {
    setFormData(prev => {
      const currentCategories = prev.topicCategories || [];
      const isSelected = currentCategories.includes(subTopic);
      
      return {
        ...prev,
        topicCategories: isSelected
          ? currentCategories.filter(c => c !== subTopic)
          : [...currentCategories, subTopic]
      };
    });
  };

  /**
   * Die zusätzlichen Spuren EINER Variante (position ≥ 1) — vorher eine eigene Sektion.
   *
   * ⚠️ Inhaltlich unverändert: dieselben Felder, dieselben Aufrufe (`addExtraTrack`,
   * `updateExtraTrack`, `removeExtraTrack`, `openBunnyPickerForExtra`), derselbe Schreibpfad.
   * Verschoben ist nur der ORT — von einem zweiten Kasten in den Aufklapper der Variante, zu der
   * sie gehören.
   */
  /**
   * Welche Fassungs-Zeile ihre Einstellungen zeigt — höchstens EINE.
   *
   * ⚠️ Bewusst eine einzelne Kennung statt einer Menge: mit vier gleichzeitig offenen Zeilen wäre
   * die Sektion wieder so lang wie die drei Kästen vorher, und Toms Satz („eine Fläche, nicht
   * drei") wäre formal erfüllt und praktisch verfehlt. Wer die zweite aufklappt, schließt die
   * erste — die Übersicht bleibt.
   */
  const [offeneFassung, setOffeneFassung] = useState<VariantSlot | null>(null);

  /** Wo der geklickte Knopf VOR dem Umschalten stand — der Ausgleich läuft im Layout-Effekt. */
  const ankerRef = useRef<{ knopf: HTMLButtonElement; top: number } | null>(null);

  /**
   * Die Zeile umschalten, OHNE dass der geklickte Knopf unter dem Finger wegspringt.
   *
   * ⚠️ Warum das nötig ist, gemessen von 💎: klickt man „Einstellungen" der zweiten Zeile, während
   * die erste offen ist, schließt sich die erste — und alles darunter rutscht um deren Höhe nach
   * oben. Gemessen 379 px. Der Knopf, den man gerade gedrückt hat, steht danach woanders; wer
   * zweimal hintereinander etwas aufklappen will, klickt beim zweiten Mal ins Leere.
   *
   * ⚠️⚠️ DER ERSTE ANLAUF (PR #761) HAT NICHT GEWIRKT, und der Grund gehört hierher statt in eine
   * Chat-Nachricht: er glich in einem `requestAnimationFrame` direkt nach `setState` aus. Das
   * läuft in React 19 zu FRÜH — der neue Baum steht dann noch nicht, der gemessene Versatz ist
   * also ≈ 0 und der Ausgleich ebenso. An der laufenden App nachgemessen: Sprung unverändert
   * −370 px, `window.scrollY` blieb bei 595. Ein Fix, der grün aussieht und nichts tut, ist
   * teurer als kein Fix.
   *
   * Der ZWEITE Anlauf (#762) war `useLayoutEffect` — richtig, aber immer noch wirkungslos:
   * er scrollte mit dem Vorgabe-Verhalten, und `index.css:24` setzt `scroll-behavior: smooth`
   * auf das Dokument. Gemessen: mit `smooth` bewegt sich gar nichts, auch nach 1200 ms.
   * Erst `behavior: 'instant'` (unten) trägt. Zwei Fehlanläufe an derselben Stelle, beide
   * gemessen statt vermutet — der Grund steht hier, damit es kein dritter wird.
   *
   * ⚠️ Zweite Korrektur zum ersten Anlauf: dort stand, das CMS scrolle „nicht `window`". Das war
   * falsch und stammte aus einer Messung, bei der die Seite zufällig ganz oben stand
   * (`scrollY === 0`, also nichts auszugleichen). Nachgemessen scrollt sehr wohl das Dokument
   * (`scrollY` 595 bei geöffnetem Formular). Die Suche nach einem scrollenden Vorfahren bleibt
   * trotzdem stehen: sie kostet nichts und trägt, falls das Layout je einen eigenen Scrollbereich
   * bekommt.
   */
  const fassungUmschalten = (slot: VariantSlot, knopf: React.RefObject<HTMLButtonElement | null>) => {
    if (knopf.current) {
      ankerRef.current = { knopf: knopf.current, top: knopf.current.getBoundingClientRect().top };
    }
    setOffeneFassung((jetzt) => (jetzt === slot ? null : slot));
  };

  useLayoutEffect(() => {
    const anker = ankerRef.current;
    ankerRef.current = null;
    if (!anker) return;

    const versatz = anker.knopf.getBoundingClientRect().top - anker.top;
    if (Math.abs(versatz) < 1) return;

    let el: HTMLElement | null = anker.knopf.parentElement;
    while (el) {
      const stil = getComputedStyle(el);
      if (/(auto|scroll)/.test(stil.overflowY) && el.scrollHeight > el.clientHeight) {
        // `instant` auch hier — aus demselben Grund wie unten.
        el.scrollBy({ top: versatz, behavior: 'instant' });
        return;
      }
      el = el.parentElement;
    }

    // ⚠️⚠️ `behavior: 'instant'` ist der ganze Fix, und ohne diese Zeile war der zweite Anlauf
    // genauso wirkungslos wie der erste. `index.css:24` setzt `scroll-behavior: smooth` auf das
    // Dokument, und ein geerbtes `smooth` macht `window.scrollBy(0, x)` hier NICHT langsam,
    // sondern WIRKUNGSLOS. An der ausgerollten Fassung nachgemessen (2026-09-01, `scrollY` 600):
    //
    //   window.scrollBy(0, -40)                          → nach 1200 ms: 600  (nichts)
    //   window.scrollBy({top: -40, behavior: 'instant'})  → nach  200 ms: 560  (wirkt)
    //
    // Inhaltlich ist `instant` ohnehin das Richtige: dieser Ausgleich soll die Position STABIL
    // halten. Eine Animation wäre der Sprung, nur langsamer.
    window.scrollBy({ top: versatz, behavior: 'instant' });
  }, [offeneFassung]);

  const renderZusatzSpuren = (slot: VariantSlot, label: string) => {
    const rows = extraTracks.map((t, i) => ({ t, i })).filter(({ t }) => t.slot === slot);
    return (
      /* ⚠️ ENTKLEIDET (2026-09-01, 💎-Befund 2): hier standen `rounded-lg border bg-secondary-50`
         — ein Kasten im Kasten, gemessen als Verschachtelungstiefe 4 mit Füllung rgb(248,250,252).
         Das war der alte zweite Kasten in neuer Position, nicht seine Auflösung. Was bleibt, ist
         eine Trennlinie und eine Beschriftung im Rang der übrigen Feld-Beschriftungen: die Spuren
         sind ein FELD dieser Variante, keine eigene Fläche. */
      <div className="space-y-3 border-t border-secondary-200 pt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-secondary-700">
            Weitere {label}-Spuren
            <span className="ml-2 font-normal text-secondary-400">
              (z. B. mehrere Naturklänge — Regen, Wald …)
            </span>
          </h4>
          <button type="button" onClick={() => addExtraTrack(slot)} className="btn btn-outline btn-sm">
            + Spur hinzufügen
          </button>
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-secondary-400">
            Keine zusätzlichen Spuren. Die oben gesetzte Datei bleibt die Standard-Spur.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map(({ t, i }) => (
              <div key={i} className="rounded-md border border-secondary-200 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={t.title}
                    onChange={(e) => updateExtraTrack(i, { title: e.target.value })}
                    placeholder="Titel (z. B. Regen)"
                    className="input flex-1"
                  />
                  <label className="flex items-center whitespace-nowrap text-sm text-secondary-700">
                    <input
                      type="checkbox"
                      checked={t.enabled}
                      onChange={(e) => updateExtraTrack(i, { enabled: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded mr-1"
                    />
                    Aktiv
                  </label>
                  <button
                    type="button"
                    onClick={() => removeExtraTrack(i)}
                    className="text-error-500 hover:text-error-600 p-1"
                    title="Spur entfernen"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={t.mp3Url}
                    onChange={(e) => updateExtraTrack(i, { mp3Url: e.target.value })}
                    placeholder="Audio-URL (z. B. de/content/...)"
                    className="input flex-1"
                    title={t.mp3Url || 'Keine URL gesetzt'}
                  />
                  <button
                    type="button"
                    onClick={() => openBunnyPickerForExtra(i)}
                    className="btn btn-outline btn-sm whitespace-nowrap"
                  >
                    <FolderIcon className="w-4 h-4 mr-1" />
                    Bunny.net
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderStreamUpload = (
    streamName: 'silent' | 'audio' | 'frequenz' | 'nature',
    label: string,
    supportsFlac: boolean = false
  ) => {
    const mp3Url = formData[`${streamName}Mp3Url` as keyof AudioFormData] as string;
    const mp3File = formData[`${streamName}Mp3File` as keyof AudioFormData] as File;
    const flacUrl = supportsFlac ? (formData[`${streamName}FlacUrl` as keyof AudioFormData] as string) : undefined;
    const flacFile = supportsFlac ? (formData[`${streamName}FlacFile` as keyof AudioFormData] as File) : undefined;
    const enabled = formData[`${streamName}Enabled` as keyof AudioFormData] as boolean;
    const mp3Ref = streamName === 'silent' ? silentMp3Ref : 
                   streamName === 'audio' ? audioMp3Ref :
                   streamName === 'frequenz' ? frequenzMp3Ref : natureMp3Ref;
    const flacRef = streamName === 'silent' ? silentFlacRef : undefined;

    const offen = offeneFassung === streamName;
    const bereichId = `${feldId}-${streamName}-einstellungen`;
    // Eine Referenz je Zeile — die Scroll-Verankerung braucht den Knopf, den die Nutzerin
    // tatsächlich gedrückt hat, nicht irgendeinen der vier.
    const knopfRef = knopfRefs[streamName];
    // Die Adresse, die das FORMULAR führt — bei Alt-Werken die einzige Wahrheit, weil sie nie
    // durch den Ingest liefen. Sie steht in der Zeile, damit das Zuklappen nichts verbirgt.
    const adressKurz = mp3File?.name || (mp3Url ? mp3Url.split('/').pop() : '');

    return (
      <div className="border border-secondary-200 rounded-lg">
        {/* ── DIE ZEILE ──────────────────────────────────────────────────────────────────────
            Schalter · Fassung · Datei · Größe · Einstellungen. Alles, was man zum ÜBERBLICKEN
            braucht, steht hier; alles, was man zum ÄNDERN braucht, hängt hinter „Einstellungen".
            ⚠️ Der Haken liegt bewusst NICHT im Aufklapper: er ist der eine Zustand, den die
            Redaktion im Vorbeigehen setzt — und ein Schalter, den man erst aufklappen muss, ist
            in einer Übersicht kein Schalter mehr. */}
        <div className="flex flex-col gap-2 p-4 lg:flex-row lg:items-center lg:gap-4">
          {/* ⚠️ FESTE Spaltenbreite ab `lg`, nicht `whitespace-nowrap` allein: die vier
              Beschriftungen sind verschieden breit („Audio" gegen „Frequenz", gemessen 88 px), und
              ohne feste Breite begann die Auskunft daneben bei jeder Zeile woanders — 💎 hat 400
              bis 426 px gemessen. Eine Spalte, die um 26 px wandert, liest sich nicht als Spalte.
              Unterhalb von `lg` steht die Beschriftung ohnehin auf eigener Zeile; dort wäre eine
              feste Breite nur verschenkter Platz. */}
          <label className="flex shrink-0 items-center whitespace-nowrap lg:w-32">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => handleInputChange(`${streamName}Enabled`, e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded mr-2"
            />
            <span className="text-sm font-semibold text-secondary-900">{label}</span>
          </label>

          {/* Was das Speicher-Feld hinter DIESEM Schalter trägt. Auskunft, kein Verbot — die
              Begründung steht ausführlich in `SchalterDeckung.tsx` und ist kein Geschmack: die
              Schalter steuern auch Alt-Werke, deren Dateien nie durch den Ingest liefen. */}
          <div className="min-w-0 flex-1">
            <SchalterDeckung
              slot={streamName}
              eintraege={fassungen?.uebersicht?.eintraege ?? null}
              laedt={fassungen?.laedt ?? false}
              fehlgeschlagen={Boolean(fassungen?.fehler)}
            />
            {/* ⚠️ Zusätzlich zur eingelesenen Datei, nicht statt ihr: die beiden sind
                verschiedene Wahrheiten (Speicher gegen Formular-Adresse), und bei Alt-Werken
                trägt NUR diese hier etwas. Sie zu verschweigen, sobald eine Speicher-Datei da
                ist, hätte die Alt-Welt unsichtbar gemacht. */}
            {/* ⚠️ Der volle Pfad hängt am `title` — und das ist auf einem Tastgerät KEIN Weg, das
                weiß diese Zeile. Sie ist deshalb nicht die einzige Quelle: die vollständige
                Adresse steht als bearbeitbares Feld hinter „Einstellungen", also genau dort, wo
                man sie ohnehin braucht, wenn man mehr will als den Dateinamen. Der `title` bleibt
                als Zugabe für die Maus, nicht als Versprechen. */}
            {adressKurz && (
              <p className="truncate text-xs text-secondary-500" title={mp3Url || adressKurz}>
                Adresse im Formular: {adressKurz}
              </p>
            )}

            {/* ── AKTIV, ABER OHNE QUELLE — hier, nicht im Balken ────────────────────────────
                ⚠️ DER ORT IST DER PUNKT. Genau diese Bedingung blockt seit jeher das Speichern
                (`handleSubmit`), aber die Meldung erschien oben rechts im Balken, während der
                Schalter weit unterhalb des Sichtfelds liegt. Tom stand davor und wusste nicht,
                warum es nicht geht: „wenn eine Tonspur hinterlegt ist, aber kein Link und man
                dann die Tonspur mit dem Checkmark anklickt, kann man nicht veröffentlichen."
                Die Entfernung zwischen Handlung und Antwort war der Fehler, nicht die Regel.

                ⛔ DAS IST EIN HINWEIS, KEINE SPERRE, und der Schalter bleibt setzbar. Drei
                Gründe, an dieser Fläche gemessen und nicht aus Geschmack:
                  1. Die Spalte, in der er steht, ist ausdrücklich „Auskunft, kein Verbot"
                     (`SchalterDeckung`, zwei Zeilen darüber). Ein blockierender Schalter
                     widerspräche der Haltung derselben Zeile.
                  2. Die Adresse wird hinter „Einstellungen" DERSELBEN Zeile eingetragen. „Erst
                     aktivieren, dann Datei" ist also der Ablauf, den das Layout vorgibt — eine
                     Sperre verböte den vorgesehenen Weg.
                  3. Die Schalter steuern auch Alt-Werke, die nie durch den Ingest liefen; dort
                     ist eine fehlende Speicher-Datei der Normalzustand, bis jemand die Adresse
                     einträgt.

                ⛔ DER SPÄTE GUARD BLEIBT. Er ist das Netz: er greift auch, wenn die Quelle
                nachträglich wieder entfernt oder der Schalter programmatisch gesetzt wird. Ein
                Test hält fest, dass er blockt, selbst wenn dieser Hinweis hier verschwindet. */}
            {enabled && !hatQuelle(streamName) && (
              <p className="mt-1 text-xs font-medium text-error-600">
                Ohne Audiodatei nicht abspielbar — Datei oder URL unter „Einstellungen" hinterlegen.
              </p>
            )}
          </div>

          <button
            ref={knopfRef}
            type="button"
            onClick={() => fassungUmschalten(streamName, knopfRef)}
            aria-expanded={offen}
            /* ⚠️ Nur wenn offen: der Bereich wird erst dann mit Inhalt gerendert, und ein
               `aria-controls`, das auf nichts zeigt, ist für eine Vorlesehilfe schlechter als
               keines — sie kündigt einen Bereich an, den sie nicht findet. 💎 hat es gemessen:
               `getElementById` lieferte zugeklappt viermal `null`. */
            aria-controls={offen ? bereichId : undefined}
            className="btn btn-outline btn-sm shrink-0 self-start whitespace-nowrap lg:self-auto"
          >
            Einstellungen
            <ChevronDownIcon
              className={`ml-1 h-4 w-4 transition-transform ${offen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </div>

        {offen && (
        <div id={bereichId} className="space-y-4 border-t border-secondary-200 p-4">
        {/* Kopfhörer-Empfehlung PRO VARIANTE (recommended | not_recommended | not_required) */}
        <div>
          <label htmlFor={`${feldId}-${streamName}-kopfhoerer`} className="block text-sm font-medium text-secondary-700 mb-1">Kopfhörer</label>
          <select
            id={`${feldId}-${streamName}-kopfhoerer`}
            value={(formData[`${streamName}Headphones` as keyof AudioFormData] as string) || ''}
            onChange={(e) => handleInputChange(`${streamName}Headphones`, e.target.value)}
            className="input w-full"
          >
            <option value="">— keine Angabe —</option>
            <option value="recommended">Empfohlen</option>
            <option value="not_recommended">Nicht empfohlen</option>
            <option value="not_required">Nicht benötigt</option>
          </select>
        </div>

        {/* Frequenzen — NUR beim Frequenz-Stream; eine pro Zeile → metadata.frequencies. */}
        {streamName === 'frequenz' && (
          <div>
            <label htmlFor={`${feldId}-${streamName}-frequenzen`} className="block text-sm font-medium text-secondary-700 mb-1">
              Frequenzen <span className="text-secondary-400">(eine pro Zeile)</span>
            </label>
            <textarea
              id={`${feldId}-${streamName}-frequenzen`}
              value={formData.frequencies || ''}
              onChange={(e) => handleInputChange('frequencies', e.target.value)}
              rows={5}
              className="input w-full"
              placeholder={'528 Hz\n369 Hz\n417 Hz\nGoldener Schnitt'}
            />
          </div>
        )}

        {/* MP3 Upload */}
        <div>
          {/* Ein Feld, nicht eine Gruppe: der gestrichelte Kasten darunter enthält nur
              Schaltflächen und ein `hidden` Datei-Feld — anspringbar ist allein dieses
              Eingabefeld, und es trägt den Wert, den Upload und Bunny ebenfalls hineinschreiben. */}
          <label htmlFor={`${feldId}-${streamName}-mp3`} className="block text-sm font-medium text-secondary-700 mb-2">
            MP3 Datei *
          </label>

          {/* URL-Eingabefeld */}
          <div className="mb-2">
            <input
              id={`${feldId}-${streamName}-mp3`}
              type="text"
              value={mp3Url || ''}
              onChange={(e) => {
                const urlFieldName = `${streamName}Mp3Url` as keyof AudioFormData;
                handleInputChange(urlFieldName, e.target.value);
                // Entferne File, wenn URL eingegeben wird
                if (e.target.value) {
                  const fileFieldName = `${streamName}Mp3File` as keyof AudioFormData;
                  handleInputChange(fileFieldName, undefined);
                }
              }}
              placeholder="Audio-URL eingeben (z.B. https://...)"
              className="input w-full mb-2"
              title={mp3Url || 'Keine URL gesetzt'}
            />
          </div>
          
          <div className="border-2 border-dashed border-secondary-300 rounded-lg p-4">
            {mp3Url || mp3File ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 flex-1">
                    <MusicalNoteIcon className="h-6 w-6 text-primary-600" />
                    <span className="text-sm text-secondary-700 truncate">
                      {mp3File?.name || mp3Url?.split('/').pop() || 'MP3 ausgewählt'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {mp3Url && (
                      <button
                        type="button"
                        onClick={() => handlePlayPreview(mp3Url, `${streamName}Mp3`, 'mp3')}
                        className="text-primary-600 hover:text-primary-700 p-1"
                        title="Vorhören"
                      >
                        {playingAudio && playingAudio.stream === `${streamName}Mp3` && playingAudio.format === 'mp3' ? (
                          <PauseIcon className="w-5 h-5" />
                        ) : (
                          <PlayIcon className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(`${streamName}Mp3`, 'mp3')}
                      className="text-error-500 hover:text-error-600"
                      title="Löschen"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                {mp3Url && (
                  <p className="text-xs text-secondary-500 truncate" title={mp3Url}>
                    {mp3Url}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => mp3Ref.current?.click()}
                    className="btn btn-outline btn-sm"
                  >
                    <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                    MP3 hochladen
                  </button>
                  <button
                    type="button"
                    onClick={() => openBunnyPicker('audio', `${streamName}Mp3`)}
                    className="btn btn-outline btn-sm"
                  >
                    <FolderIcon className="w-4 h-4 mr-2" />
                    Von Bunny.net
                  </button>
                </div>
              </div>
            )}
            <input
              ref={mp3Ref}
              type="file"
              accept="audio/mpeg,audio/mp3,.mp3"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(file, `${streamName}Mp3`, 'mp3');
                }
              }}
              className="hidden"
            />
          </div>
        </div>

        {/* FLAC Upload (nur für Silent) */}
        {supportsFlac && (
          <div>
            <label htmlFor={`${feldId}-${streamName}-flac`} className="block text-sm font-medium text-secondary-700 mb-2">
              FLAC Datei (optional)
            </label>

            {/* URL-Eingabefeld */}
            <div className="mb-2">
              <input
                id={`${feldId}-${streamName}-flac`}
                type="text"
                value={flacUrl || ''}
                onChange={(e) => {
                  const urlFieldName = `${streamName}FlacUrl` as keyof AudioFormData;
                  handleInputChange(urlFieldName, e.target.value);
                  // Entferne File, wenn URL eingegeben wird
                  if (e.target.value) {
                    const fileFieldName = `${streamName}FlacFile` as keyof AudioFormData;
                    handleInputChange(fileFieldName, undefined);
                  }
                }}
                placeholder="FLAC-URL eingeben (z.B. https://...)"
                className="input w-full mb-2"
              />
            </div>
            
            <div className="border-2 border-dashed border-secondary-300 rounded-lg p-4">
              {flacUrl || flacFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1">
                      <MusicalNoteIcon className="h-6 w-6 text-primary-600" />
                      <span className="text-sm text-secondary-700 truncate">
                        {flacFile?.name || flacUrl?.split('/').pop() || 'FLAC ausgewählt'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {flacUrl && (
                        <button
                          type="button"
                          onClick={() => handlePlayPreview(flacUrl, `${streamName}Flac`, 'flac')}
                          className="text-primary-600 hover:text-primary-700 p-1"
                          title="Vorhören"
                        >
                          {playingAudio && playingAudio.stream === `${streamName}Flac` && playingAudio.format === 'flac' ? (
                            <PauseIcon className="w-5 h-5" />
                          ) : (
                            <PlayIcon className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(`${streamName}Flac`, 'flac')}
                        className="text-error-500 hover:text-error-600"
                        title="Löschen"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  {flacUrl && (
                    <p className="text-xs text-secondary-500 truncate">
                      {flacUrl}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => flacRef?.current?.click()}
                      className="btn btn-outline btn-sm"
                    >
                      <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                      FLAC hochladen
                    </button>
                    <button
                      type="button"
                      onClick={() => openBunnyPicker('audio', 'silentFlac')}
                      className="btn btn-outline btn-sm"
                    >
                      <FolderIcon className="w-4 h-4 mr-2" />
                      Von Bunny.net
                    </button>
                  </div>
                </div>
              )}
              <input
                ref={flacRef}
                type="file"
                accept="audio/flac,audio/x-flac,.flac"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file, `${streamName}Flac`, 'flac');
                }}
                className="hidden"
              />
            </div>
          </div>
        )}

        {/* Die zusätzlichen Spuren DIESER Variante — vorher eine eigene Sektion weiter unten.
            ⚠️ Sie gehören hierher, nicht in einen zweiten Kasten: es sind Spuren DERSELBEN
            Variante („mehrere Naturklänge unter Nature"). Getrennt aufgestellt zwangen sie dazu,
            zwei Flächen im Kopf zusammenzuhalten — genau das, was diese Runde beseitigt. */}
        {renderZusatzSpuren(streamName, label)}
        </div>
        )}
      </div>
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6 pb-24">
        {/* Sektion 1: Grunddaten */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Grunddaten</h2>

          {/* Produktkategorie */}
          <div>
            <label htmlFor={`${feldId}-produktkategorie`} className="block text-sm font-medium text-secondary-700 mb-2">
              Produktkategorie *
            </label>
            <select
              id={`${feldId}-produktkategorie`}
              value={formData.productCategory || ''}
              onChange={(e) => handleInputChange('productCategory', (e.target.value || undefined) as ProductType | undefined)}
              className="input w-full"
              required
            >
              <option value="">Bitte wählen...</option>
              {/* ⚠️ `audioFormularProduktarten`, NICHT `audioProductTypes`: MindBook fliegt aus
                  der Auswahl dieses Formulars (Tom-Entscheid 2026-09-01) — MindBooks werden unter
                  „MindBooks" gepflegt, mit eigenen Feldern und eigener Route. Die Taxonomie
                  bleibt unangetastet, und die Normalisierung oben liest weiter die volle Liste,
                  damit ein Bestandswert nicht verlorengeht. */}
              {audioFormularProduktarten.map(category => (
                <option key={category} value={category}>
                  {ProductTypeLabels[category]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-secondary-500">
              Eine Produktkategorie muss ausgewählt werden, damit der Inhalt im Backend gefunden werden kann.
            </p>
          </div>

          {/* Titel */}
          <div>
            <label htmlFor={`${feldId}-titel`} className="block text-sm font-medium text-secondary-700 mb-2">
              Titel *
            </label>
            {/* ⚠️ `aria-invalid` und `aria-describedby` sind hier keine Zierde: ohne sie steht der
                Hinweis zwar sichtbar unter dem Feld, eine Vorlesehilfe verbindet ihn aber mit
                nichts — sie liest „Titel, Eingabefeld" und schweigt über den Grund. Beide Attribute
                stehen bewusst nur an, WENN ein Hinweis anliegt; ein dauerhaftes
                `aria-invalid="false"` ist Lärm im Baum. */}
            <input
              ref={titelRef}
              id={`${feldId}-titel`}
              type="text"
              value={formData.title}
              onChange={(e) => {
                handleInputChange('title', e.target.value);
                // Der Hinweis geht mit der Eingabe weg, nicht erst beim nächsten Absenden — sonst
                // steht eine widerlegte Fehlermeldung neben einem längst gefüllten Feld.
                if (titelFehler) setTitelFehler(null);
              }}
              className="input w-full"
              placeholder="Audio-Titel eingeben..."
              required
              aria-invalid={titelFehler ? true : undefined}
              aria-describedby={titelFehler ? titelFehlerId : undefined}
            />
            {titelFehler && (
              <p id={titelFehlerId} className="mt-2 text-sm text-error-600">
                {titelFehler}
              </p>
            )}
          </div>

          {/* ── ZWEI Beschreibungen statt drei (Bereinigung 2026-09-01, Tom-Zielbild) ────────
              Hier standen drei Kästen: „Beschreibung" (die Spalte `description`, von der
              Produktseite NIE gelesen), „Kurzbeschreibung" und „Langbeschreibung" (die, die die
              Produktseite tatsächlich zeigte). Drei Felder für zwei Bedeutungen — und das
              wichtigste davon trug den unscheinbarsten Namen.
              Jetzt: „Beschreibung" IST die lange (samt Platz der alten Lang-Box) und wird
              angezeigt; „Kurzbeschreibung" bleibt für Teaser und Übersicht.
              ⚠️ Die Lang-Box ist weg — und seit dem CONTRACT (#769) auch das Feld dahinter:
              `longDescription` ist aus Daten und Backend ausgetragen. Wer die Box „zurückbaut",
              legt die dritte Wahrheit wieder an; die Backend-Wache
              `services/__tests__/longDescriptionAusgetragen.test.ts` hält dagegen. */}
          <div>
            <label htmlFor={`${feldId}-beschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">
              Beschreibung
            </label>
            <textarea
              id={`${feldId}-beschreibung`}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={6}
              className="input w-full"
              placeholder="Ausführliche Beschreibung — dieser Text steht auf der Produktseite."
            />
          </div>

          {/* Kurzbeschreibung */}
          <div>
            <label htmlFor={`${feldId}-kurzbeschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">
              Kurzbeschreibung
            </label>
            <textarea
              id={`${feldId}-kurzbeschreibung`}
              value={formData.shortDescription || ''}
              onChange={(e) => handleInputChange('shortDescription', e.target.value)}
              rows={3}
              className="input w-full"
              placeholder="Kurze Beschreibung für Teaser und Übersicht..."
            />
          </div>

          {/* Affirmationen — eine pro Zeile; Save splittet zu metadata.affirmations (string[]). */}
          <div>
            <label htmlFor={`${feldId}-affirmationen`} className="block text-sm font-medium text-secondary-700 mb-2">
              Affirmationen <span className="text-secondary-400">(eine pro Zeile)</span>
            </label>
            <textarea
              id={`${feldId}-affirmationen`}
              value={formData.affirmations || ''}
              onChange={(e) => handleInputChange('affirmations', e.target.value)}
              rows={8}
              className="input w-full"
              placeholder={'Ich liebe und akzeptiere meinen Körper, so wie er ist.\nMein Körper ist mein sicheres Zuhause.\n...'}
            />
          </div>
        </section>

        {/* ══ Sektion 2: Audio-Fassungen — EINE Fläche ══════════════════════════════════════
            ⚠️ Hier standen bis zum 2026-08-31 DREI getrennte Flächen: „Audio-Streams" (die vier
            Schalter), „Zusätzliche Varianten-Spuren" (ein zweiter Kasten mit denselben vier
            Varianten) und, außerhalb des Formulars, „Eingelesene Fassungen" (der Speicher-
            Bestand). Wer wissen wollte, was an einer Variante hängt, musste drei Stellen im Kopf
            zusammenhalten — Toms Satz dazu: „Ich will EINE Fläche sehen, nicht drei."
            Jetzt: eine Zeile je Fassung. Schalter · Fassung · Datei · Größe · Einstellungen.
            ⛔ Der SCHREIBPFAD ist unverändert: dieselben Formularfelder, dieselben Aufrufe,
            dieselbe Nutzlast (`contentData.streams`/`variantTracks` via Dual-Write). Diese Runde
            ist reine Darstellung — sie bewegt keine Zeile in der Datenbank. */}
        <section className="card p-4 md:p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Audio-Fassungen</h2>
            <p className="mt-1 max-w-prose text-sm text-secondary-500">
              Eine Zeile je Variante: der Schalter, die eingelesene Datei dahinter und — hinter
              „Einstellungen" — Kopfhörer, Adresse und weitere Spuren derselben Variante.
            </p>
          </div>

          {/* ⚠️ Diese Bloecke standen bis zum 2026-09-01 hier INLINE (Hinweise, nicht
              zugeordnete Dateien, Fehlerpfad). Sie sind in `FassungsUebersicht` gezogen, weil das
              MindBook-Formular dieselbe Flaeche braucht — Extraktion statt Abschrift. Was hier
              bleibt, ist die Deckungszeile JE SCHALTER: die haengt an den vier Varianten-Slots,
              und die hat nur diese Welt.
              `mitTonListe` ist hier bewusst AUS: jede Ton-Datei steht bereits an ihrem Schalter,
              eine zweite Liste zeigte dasselbe ein zweites Mal — genau Toms Befund
              („eine Flaeche, nicht drei"), der nicht durch die Hintertuer zurueckkommen soll. */}
          <FassungsUebersicht zustand={fassungen ?? LEERER_FASSUNGSZUSTAND} />

          {/* ⚠️ Aus `VARIANT_SLOTS` erzeugt, nicht vier Mal von Hand geschrieben: die Anzeigenamen
              wohnen in `AudioStreamTypeLabels`, und genau diese Tabelle stand in diesem Projekt
              schon einmal FÜNFMAL als Handkopie daneben — mit dem Ergebnis, dass „Nature" und
              „Natur" auseinanderliefen. Die Reihenfolge kommt aus `VARIANT_SLOT_ORDER`.
              `supportsFlac` gilt nur für Silent: dort liegt die verlustfreie Fassung. */}
          <div className="space-y-3">
            {VARIANT_SLOTS.map(({ slot, label }) =>
              <React.Fragment key={slot}>{renderStreamUpload(slot, label, slot === 'silent')}</React.Fragment>,
            )}
          </div>

        </section>

        {/* Sektion 3: Metadaten */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Metadaten</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${feldId}-interpret`} className="block text-sm font-medium text-secondary-700 mb-2">
              Künstler / Interpret
            </label>
            <select
              id={`${feldId}-interpret`}
              value={formData.artistId || ''}
              onChange={(e) => handleInputChange('artistId', e.target.value)}
              className="input w-full"
            >
              <option value="">— kein Interpret —</option>
              {artists.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.status !== 'PUBLISHED' ? ' (Entwurf)' : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-secondary-500">
              Interpreten pflegen Sie unter „Interpreten". Der angezeigte Name wird daraus abgeleitet.
            </p>
          </div>
          <div>
            <label htmlFor={`${feldId}-album`} className="block text-sm font-medium text-secondary-700 mb-2">
              Album
            </label>
            <input
              id={`${feldId}-album`}
              type="text"
              value={formData.album || ''}
              onChange={(e) => handleInputChange('album', e.target.value)}
              className="input w-full"
              placeholder="Album..."
            />
          </div>
          <div>
            <label htmlFor={`${feldId}-sprache`} className="block text-sm font-medium text-secondary-700 mb-2">
              Sprache
            </label>
            <input
              id={`${feldId}-sprache`}
              type="text"
              value={formData.language || ''}
              onChange={(e) => handleInputChange('language', e.target.value)}
              className="input w-full"
              placeholder="Sprache..."
            />
          </div>
        </div>

        {/* Cover Image */}
        <div>
          {/* Gruppe statt Feld: hier steht kein Eingabefeld, nur Schaltflächen (hochladen,
              Bunny, entfernen) und ein `hidden` Datei-Feld, das nicht anspringbar ist. */}
          <span id={`${feldId}-cover`} className="block text-sm font-medium text-secondary-700 mb-2">
            Cover Bild
          </span>
          <div role="group" aria-labelledby={`${feldId}-cover`} className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center">
            {coverImagePreview ? (
              <div className="relative">
                <img
                  src={coverImagePreview}
                  alt="Cover Preview"
                  className="mx-auto h-32 w-32 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeFile('cover')}
                  className="absolute -top-2 -right-2 bg-error-500 text-white rounded-full p-1 hover:bg-error-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div>
                <PhotoIcon className="mx-auto h-12 w-12 text-secondary-400" />
                <div className="mt-2 space-y-2">
                  <button
                    type="button"
                    onClick={() => coverImageRef.current?.click()}
                    className="btn btn-outline btn-sm mr-2"
                  >
                    <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                    Bild hochladen
                  </button>
                  <button
                    type="button"
                    onClick={() => openBunnyPicker('image')}
                    className="btn btn-outline btn-sm"
                  >
                    <FolderIcon className="w-4 h-4 mr-2" />
                    Von Bunny.net
                  </button>
                </div>
              </div>
            )}
            <input
              ref={coverImageRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setFormData(prev => ({ ...prev, coverImage: file, coverImageUrl: undefined }));
                  setCoverImagePreview(URL.createObjectURL(file));
                }
              }}
              className="hidden"
            />
          </div>
        </div>

        {/* Dauer */}
        <div>
          <label htmlFor={`${feldId}-dauer`} className="block text-sm font-medium text-secondary-700 mb-2">
            Dauer
          </label>
          <input
            id={`${feldId}-dauer`}
            type="text"
            value={formData.duration || ''}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            className="input w-full"
            placeholder="z.B. 30 Minuten, 1 Stunde, etc."
          />
        </div>

        {/* Kopfhörer-Empfehlung wird jetzt PRO VARIANTE in Sektion „Audio-Streams" gepflegt
            (renderStreamUpload → headphonesByVariant), kein Produkt-Level-Feld mehr. */}
        </section>

        {/* Sektion 4: Klassifizierung */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Klassifizierung</h2>

          {/* Themen-Kategorien */}
          <div>
            <h3 className="text-sm font-medium text-secondary-700 mb-2">Themen-Kategorien</h3>
            <TopicCategoryPicker
              selected={formData.topicCategories || []}
              onToggle={handleSubTopicToggle}
            />
          </div>

          {/* Tags — die Bedienung wohnt seit dem 2026-09-08 EINMAL in `SchlagwortFeld`.
              Vorher stand dieser Block hier und in der Schwester-Vorlage von Hand, gemessen
              zeichengleich (Kommentare geleert, gediffed: Exit 0; Positivkontrolle mit einem
              geänderten Zeichen: Exit 1). Gehoben wurde er, weil das MindBook-Formular eine
              dritte Kopie gebraucht hätte. */}
          <SchlagwortFeld
            werte={formData.tags || []}
            onÄndern={(tags) => setFormData((prev) => ({ ...prev, tags }))}
          />
        </section>

        {/* Sektion 5: Veröffentlichung & Flags */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Veröffentlichung &amp; Flags</h2>

        {/* Zugriff (einziges Feld: Premium an = Premium, aus = Free) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PremiumFeld
            wert={formData.isPremium}
            onÄndern={(wert) => handleInputChange('isPremium', wert)}
            gesperrt={!darfModerieren}
          />

          {/* ⚠️ DIESES FELD HAT HIER GEFEHLT, und deshalb liess sich ein Audio ueber das Formular
              nicht veroeffentlichen (Tom, 2026-09-04: „Wieso kann ich das Audio Subliminal
              Harmonische Beziehung nicht veroeffentlichen?"). Der Status lief schon immer DURCH
              das Formular — `AudioForm.tsx:107` erhaelt ihn beim Speichern, damit ein Save nicht
              entpublisht — nur setzen konnte ihn niemand. Veroeffentlicht wurde bis dahin
              ausschliesslich ueber die Inhalte-Liste (`POST /api/content/:id/publish`).

              ⛔ AUSWAHLFELD, KEIN ZWEI-ZUSTANDS-KNOPF: Audio traegt `ContentStatus` mit VIER Stufen,
              genau wie Video und MindBook. Album/Interpret/Playlist haben eigene Zwei-Stufen-Enums
              und deshalb dort einen Knopf; hier verschluckte er `ARCHIVED` — einen Zustand, den die
              Inhalte-Liste setzen kann — und waere die vierte Darstellung desselben Enums.

              Waehlbar sind nur die drei Stufen aus `STATUS_WAEHLBAR`; „Abgelehnt" vergibt die
              Pruefung ueber `POST /api/content/:id/reject` (ADMIN/MODERATOR), nicht der Verfasser.
              Ist der Datensatz bereits abgelehnt, steht die Stufe GESPERRT dabei: ohne sie zeigte
              das Auswahlfeld eine leere Zeile, und der Zustand waere unsichtbar. Begruendung und
              Texte: `STATUS_WAEHLBAR` / `STATUS_ANZEIGE` in `types/index.ts`. Wortgleich zu
              `VideoForm` und `MindBookForm` — dasselbe Enum, dieselbe Bedienung. */}
          <div>
            <label htmlFor={`${feldId}-status`} className="block text-sm font-medium text-secondary-700 mb-2">
              Status
            </label>
            <select
              id={`${feldId}-status`}
              value={formData.status || ContentStatus.DRAFT}
              onChange={(e) => handleInputChange('status', e.target.value as ContentStatus)}
              aria-describedby={darfModerieren ? undefined : `${feldId}-status-hinweis`}
              className="input w-full cursor-pointer"
            >
              {STATUS_WAEHLBAR.map((stufe) => (
                <option key={stufe} value={stufe} disabled={stufeGesperrt(stufe, darfModerieren)}>
                  {stufenText(stufe, darfModerieren, bestandStatus)}
                </option>
              ))}
              {formData.status === ContentStatus.REJECTED && (
                <option value={ContentStatus.REJECTED} disabled>
                  {STATUS_ANZEIGE[ContentStatus.REJECTED]?.text ?? ContentStatus.REJECTED}
                </option>
              )}
            </select>
            {!darfModerieren && (
              <ModerationsHinweis id={`${feldId}-status-hinweis`} warnung={rückfall}>
                {rückfall ? MODERATIONS_TEXTE.rückfallHinweis : MODERATIONS_TEXTE.statusHinweis}
              </ModerationsHinweis>
            )}
          </div>
        </div>
        </section>

        {/* Form Actions */}
        <div className="fixed bottom-0 right-0 left-64 z-30 flex justify-end items-center gap-3 px-6 lg:px-8 py-3 md:py-4 bg-secondary-50/95 backdrop-blur-sm border-t border-secondary-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          {/* Gefahren-Aktion: Löschen — nur im Edit-Modus, klar abgesetzt links */}
          {/* Löschen ist Moderation (`DELETE /:id` hinter `authorize(['ADMIN', 'MODERATOR'])`, aus der Route
              gelesen) — für die Redaktion endet der Knopf in 403. BACKLOG `cms-listenknoepfe-redaktion`. */}
          {audioId && onDelete && darfModerieren && (
            <button
              type="button"
              onClick={() => onDelete(audioId)}
              className="mr-auto inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-600 hover:bg-error-50 hover:border-error-400 focus:outline-none focus:ring-2 focus:ring-error-500 disabled:opacity-50"
              disabled={isLoading}
            >
              <TrashIcon className="w-4 h-4" />
              Audio löschen
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline btn-md"
            disabled={isLoading}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-md"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner w-4 h-4 mr-2" />
                Speichern...
              </>
            ) : (
              'Audio speichern'
            )}
          </button>
        </div>
      </form>

      {/* Bunny.net File Picker */}
      <BunnyFilePicker
        isOpen={showBunnyPicker}
        onClose={() => setShowBunnyPicker(false)}
        onSelect={handleBunnyFileSelect}
        fileType={bunnyPickerContext.type}
        allowUrlInput={true}
        title={bunnyPickerContext.type === 'image' ? 'Bild von Bunny.net auswählen' : 'Audio von Bunny.net auswählen'}
      />
      
      {/* Audio Player für Vorhör-Funktion (versteckt).
          ⚠️ Hier hingen zusätzlich `onLoadStart` und `onCanPlay`, die beide nichts anderes taten,
          als die eigene Meldung dieses Formulars wegzuräumen. Auf der geteilten Quelle der Seite
          wären sie schädlich: ein Druck auf „Vorhören" löschte damit auch eine stehende
          Speicher-Fehlermeldung, ohne dass die Nutzerin sie weggeklickt hat. Volle Begründung
          steht an `handlePlayPreview`. */}
      <audio
        ref={audioPlayerRef}
        onEnded={() => setPlayingAudio(null)}
        onError={(e) => {
          const audioElement = e.currentTarget;
          let errorMessage = 'Fehler beim Laden des Audios';
          
          if (audioElement.error) {
            switch (audioElement.error.code) {
              case MediaError.MEDIA_ERR_ABORTED:
                errorMessage = 'Audio-Wiedergabe wurde abgebrochen';
                break;
              case MediaError.MEDIA_ERR_NETWORK:
                errorMessage = `Netzwerkfehler. Die Audio-Datei konnte nicht geladen werden. Möglicherweise ein CORS-Problem oder die Datei existiert nicht. URL: ${audioElement.src}`;
                break;
              case MediaError.MEDIA_ERR_DECODE:
                errorMessage = 'Audio-Datei konnte nicht dekodiert werden. Möglicherweise ist die Datei beschädigt.';
                break;
              case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED: {
                // Prüfe, ob es ein URL-Problem ist
                const url = audioElement.src;
                if (!url || url === '') {
                  errorMessage = 'Keine URL angegeben';
                } else if (!url.startsWith('http://') && !url.startsWith('https://')) {
                  errorMessage = `Ungültige URL-Format: ${url}`;
                } else {
                  errorMessage = `Audio-Format wird nicht unterstützt oder die URL ist ungültig. Bitte prüfen Sie, ob die URL korrekt ist und die Datei existiert. URL: ${url}`;
                }
                break;
              }
              default:
                errorMessage = `Unbekannter Fehler (Code: ${audioElement.error.code})`;
            }
          }
          
          console.error('Fehler beim Abspielen des Audios:', {
            error: audioElement.error,
            src: audioElement.src,
            networkState: audioElement.networkState,
            readyState: audioElement.readyState,
            errorCode: audioElement.error?.code,
            errorMessage: audioElement.error?.message
          });
          
          notify('error', errorMessage);
          setPlayingAudio(null);
        }}
        className="hidden"
        preload="metadata"
      />

      {/* Die Rückfrage vor dem Speichern. Hängt sich per Portal an `document.body`; die Stelle
          im Baum entscheidet nichts über die Darstellung, und ohne offene Frage rendert der
          Baustein nichts. Die gerechneten Stapelwerte stehen oben am Haken. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

      {/* ⚠️ Hier stand der eigene Balken dieses Formulars (`<Benachrichtigung … schwebend />` mit
          eigenem Zustand). Er ist weg, nicht verschoben: den Balken hält jetzt `AudioManagement`,
          EINMAL und außerhalb seiner Formular-Weiche. Die gerechnete Begründung für die Lage oben
          rechts — Knopfleiste dieses Formulars `fixed bottom-0 … z-30`, Datei-Wähler
          `.bunny-file-picker-overlay` `fixed inset-0 z-50`, Balken `z-[60]` — steht dort am
          Einsatzort weiter. Wer an dieser Datei die Knopfleiste oder den Wähler in ihrer
          Stapelordnung anfasst, muss dort nachziehen. */}
    </>
  );
};
